export type PortType = "physical" | "virtual";

export type TimeRangePreset = {
  timeRange: string;
  changeIntervals: { high: string; medium: string; low: string };
};

export function formatDuration(dur: string): string {
  const parts: string[] = [];
  const regex = /(\d+)(d|h|m|s)/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(dur))) {
    const [, n, u] = m;
    const one = n === "1";
    parts.push(
      u === "d" ? (one ? "1 Day" : `${n} Days`) :
      u === "h" ? (one ? "1 Hour" : `${n} Hours`) :
      u === "m" ? (one ? "1 Minute" : `${n} Minutes`) :
      u === "s" ? (one ? "1 Second" : `${n} Seconds`) :
      `${n}${u}`
    );
  }
  return parts.join(" ");
}

export function toSeconds(dur: string): number {
  const m = dur.match(/^(\d+)([smhd])$/);
  if (!m) return Number.POSITIVE_INFINITY;
  const n = parseInt(m[1], 10);
  const u = m[2];
  return u === "s" ? n
       : u === "m" ? n * 60
       : u === "h" ? n * 3600
       : u === "d" ? n * 86400
       : Number.POSITIVE_INFINITY;
}

/** ---------------- PHYSICAL (original) ---------------- */
export const PRESETS_PHYSICAL = [
  { timeRange: "5m",  changeIntervals: { high: "5s",  medium: "10s", low: "15s" } },
  { timeRange: "10m", changeIntervals: { high: "5s",  medium: "10s", low: "20s" } },
  { timeRange: "30m", changeIntervals: { high: "10s", medium: "20s", low: "30s" } },
  { timeRange: "1h",  changeIntervals: { high: "20s", medium: "30s", low: "1m"  } },
  { timeRange: "6h",  changeIntervals: { high: "2m",  medium: "3m",  low: "5m"  } },
  { timeRange: "12h", changeIntervals: { high: "5m",  medium: "7m",  low: "10m" } },
  { timeRange: "1d",  changeIntervals: { high: "7m",  medium: "10m", low: "15m" } },
  { timeRange: "7d",  changeIntervals: { high: "1h",  medium: "2h",  low: "3h"  } },
  { timeRange: "15d", changeIntervals: { high: "2h",  medium: "3h",  low: "4h"  } },
  { timeRange: "30d", changeIntervals: { high: "4h",  medium: "6h",  low: "8h"  } },
  { timeRange: "60d", changeIntervals: { high: "8h",  medium: "12h", low: "24h" } },
] as const;

/** ---------------- VIRTUAL (new rules) ----------------
 * Time range: 30m..7d
 * Interval : 5m..7h
 */
export const PRESETS_VIRTUAL = [
  { timeRange: "30m", changeIntervals: { high: "5m",  medium: "7m",  low: "10m" } },
  { timeRange: "1h",  changeIntervals: { high: "5m",  medium: "7m",  low: "10m" } },
  { timeRange: "6h",  changeIntervals: { high: "7m",  medium: "10m", low: "30m" } },
  { timeRange: "12h", changeIntervals: { high: "30m", medium: "1h",  low: "2h"  } },
  { timeRange: "1d",  changeIntervals: { high: "2h",  medium: "4h",  low: "7h"  } },
  { timeRange: "7d",  changeIntervals: { high: "4h",  medium: "6h",  low: "7h"  } },
] as const;

/** Literal unions extracted from presets (NO generics) */
type PhysicalPreset        = typeof PRESETS_PHYSICAL[number];
type VirtualPreset         = typeof PRESETS_VIRTUAL[number];
type AnyPreset             = PhysicalPreset | VirtualPreset;

export type TimeRangeKey   = PhysicalPreset["timeRange"] | VirtualPreset["timeRange"];
export type IntervalLiteral =
  PhysicalPreset["changeIntervals"][keyof PhysicalPreset["changeIntervals"]] |
  VirtualPreset["changeIntervals"][keyof VirtualPreset["changeIntervals"]];

export type Buckets = { high: IntervalLiteral; medium: IntervalLiteral; low: IntervalLiteral };

export function buildControls(portType?: PortType) {
  const presets = (portType === "virtual" ? PRESETS_VIRTUAL : PRESETS_PHYSICAL) as readonly AnyPreset[];

  const order = presets.map(p => p.timeRange) as TimeRangeKey[];
  const map = presets.reduce((acc, p) => {
    acc[p.timeRange as TimeRangeKey] = p.changeIntervals as Buckets;
    return acc;
  }, {} as Record<TimeRangeKey, Buckets>);

  const minTimeRange = (portType === "virtual" ? "30m" : order[0]) as TimeRangeKey;
  const maxTimeRange = (portType === "virtual" ? "7d"  : order[order.length - 1]) as TimeRangeKey;

  const minInterval  = (portType === "virtual" ? "5m" : map[order[0]].high) as IntervalLiteral;
  const maxInterval  = (portType === "virtual" ? "7h" : map[order[order.length - 1]].low) as IntervalLiteral;

  return { presets, map, order, minTimeRange, maxTimeRange, minInterval, maxInterval };
  
}

export type TimeRangeOption = { value: TimeRangeKey;   label: string };
export type IntervalOption  = { value: IntervalLiteral; label: string };

export function getTimeRangeOptions(presets: readonly AnyPreset[]) {
  return presets.map(p => ({
    value: p.timeRange as TimeRangeKey,
    label: formatDuration(p.timeRange),
  }));
}

export function getIntervalOptions(
    portType: PortType | undefined,
    tr: TimeRangeKey,
    map: Record<TimeRangeKey, Buckets>,
    minInterval: IntervalLiteral,
    maxInterval: IntervalLiteral
  ): IntervalOption[] {
    const b = map[tr];
    if (!b) return [];

    const raw = (["high","medium","low"] as const).map(k => b[k]) as IntervalLiteral[];

    if (portType !== "virtual") {
      return raw.map(v => ({ value: v, label: formatDuration(v) }));
    }
  
    const lo = toSeconds(minInterval);
    const hi = toSeconds(maxInterval);
  
    let filtered = raw.filter(v => {
      const s = toSeconds(v);
      return s >= lo && s <= hi;
    }) as IntervalLiteral[];
  
    if (filtered.length === 0) filtered = [minInterval];
  
    const uniq = Array.from(new Set(filtered as string[])) as IntervalLiteral[];
    uniq.sort((a, b) => toSeconds(a) - toSeconds(b));
  
    return uniq.map(v => ({ value: v, label: formatDuration(v) }));
  }
